import { FormEvent, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { RATECARDS_QUERY, RATECARD_QUERY } from '../../queries/ratecards';
import ToastNotification from '../../components/ToastNotification';

export default function RatecardsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [ratecardName, setRatecardName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRatecard, setSelectedRatecard] = useState<any>(null);
  const formRef = useRef<HTMLFormElement>();

  // GraphQL hooks
  const {
    data: ratecardsData,
    loading: loadingRatecards,
    refetch: refetchRatecards,
  } = useQuery(RATECARDS_QUERY);
  const { data: selectedRatecardData, loading: loadingSelected } = useQuery(
    RATECARD_QUERY,
    {
      variables: { id: selectedRatecard?.id },
      skip: !selectedRatecard?.id,
    }
  );

  const ratecards = ratecardsData?.ratecards || [];

  const handleUpload = async () => {
    if (!file || !ratecardName) return;

    setUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', ratecardName);

      // Use credentials: 'include' to send cookies (like the refresh token)
      const response = await fetch('/api/ratecard/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Upload failed with status ${response.status}`
        );
      }

      const data = await response.json();
      console.log('Upload successful:', data);

      setSuccessMessage('Ratecard uploaded successfully');

      setRatecardName('');
      if (formRef.current) {
        formRef.current.reset();
      }

      await refetchRatecards();
    } catch (err) {
      console.error('Upload error:', err);
      if (err instanceof Error && err.message?.includes('E11000')) {
        setErrorMessage('You already have a ratecard with that name');
      } else {
        setErrorMessage(err instanceof Error ? err.message : 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRatecardClick = (ratecard: any) => {
    setSelectedRatecard(ratecard);
  };

  useEffect(
    function removeSuccessAfterTimeout() {
      if (successMessage) {
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    },
    [successMessage]
  );

  return (
    <>
      <div className="p-4 max-w-5xl mx-auto">
        <h3 className="text-lg font-semibold mb-2">Upload Ratecard</h3>
        <form onSubmit={(e: FormEvent) => e.preventDefault()} ref={formRef}>
          <input
            type="text"
            placeholder="e.g., Macys 2025 Ratecard"
            value={ratecardName}
            onChange={(e) => setRatecardName(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-2"
          />
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full mb-1"
          />
          <p className="text-sm text-gray-600 mb-2">
            Please upload an Excel file (.xlsx format only)
          </p>
          <button
            onClick={handleUpload}
            disabled={uploading || !file || !ratecardName}
            className="bg-primary text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </form>

        <h4 className="mt-6 font-medium">Existing Ratecards</h4>
        {loadingRatecards ? (
          <p>Loading...</p>
        ) : (
          <ul className="list-disc list-inside">
            {ratecards.map((r: any) => (
              <li
                key={r.id}
                className="cursor-pointer text-blue-700 hover:underline"
                onClick={() => handleRatecardClick(r)}
              >
                {r.name}
              </li>
            ))}
          </ul>
        )}

        {loadingSelected && <p className="mt-4">Loading ratecard data...</p>}
        {selectedRatecardData?.ratecard?.rows?.length > 0 && (
          <div className="overflow-x-auto mt-4 border rounded">
            <table className="min-w-full text-sm text-left table-auto">
              <thead className="bg-gray-100 border-b font-semibold">
                <tr>
                  <th className="px-4 py-2">Ratecard Name</th>
                  <th className="px-4 py-2">Rate Group</th>
                  <th className="px-4 py-2">Group Type</th>
                  <th className="px-4 py-2">Cost</th>
                  <th className="px-4 py-2">Units</th>
                  <th className="px-4 py-2">Min</th>
                  <th className="px-4 py-2">Max</th>
                </tr>
              </thead>
              <tbody>
                {selectedRatecardData.ratecard.rows.map(
                  (row: any, idx: number) => (
                    <tr key={idx} className="border-b">
                      <td className="px-4 py-2">{row.ratecardName}</td>
                      <td className="px-4 py-2">{row.rateGroup}</td>
                      <td className="px-4 py-2">{row.groupType}</td>
                      <td className="px-4 py-2">{row.cost}</td>
                      <td className="px-4 py-2">{row.units}</td>
                      <td className="px-4 py-2">{row.min}</td>
                      <td className="px-4 py-2">{row.max}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!uploading && (successMessage || errorMessage) && (
        <ToastNotification
          message={successMessage || errorMessage}
          success={successMessage && !errorMessage}
          error={errorMessage && !successMessage}
          show={Boolean(successMessage || errorMessage)}
        />
      )}
    </>
  );
}